/**
 * SlugEditor — the ⋯ "Change URL…" popover. Overrides a page's URL slug (which
 * otherwise derives from its name). Small fixed-position form anchored at the
 * menu point; a backdrop closes it. react-strict-dom (html.* + css.create) with
 * Duro form parts.
 */
import { useState } from "react";
import { css, html } from "react-strict-dom";
import { Button, Field, Input, InputGroup } from "@duro-app/ui";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { layers } from "@duro-app/tokens/tokens/layers.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";
import { radii, spacing } from "@duro-app/tokens/tokens/spacing.css";
import { typeScale } from "@duro-app/tokens/tokens/typography.css";

/** Normalise free text to a URL-safe slug (matches serializeSiteDoc's slugify). */
function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const s = css.create({
  backdrop: {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: layers.popupBackdrop,
  },
  pop: {
    position: "fixed",
    zIndex: layers.popup,
    minWidth: sizes.popoverWSm,
    backgroundColor: colors.bgCard,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.border,
    borderRadius: radii.md,
    boxShadow: shadows.lg,
    padding: spacing.ms,
    fontSize: typeScale.fontSize2,
    color: colors.text,
  },
  at: (x: number, y: number) => ({
    left: x,
    top: y,
  }),
  actions: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.sm,
    marginTop: spacing.ms,
  },
});

export function SlugEditor({
  x,
  y,
  label,
  slugOverride,
  domain = "yoursite.com",
  onSave,
  onReset,
  onClose,
}: {
  x: number;
  y: number;
  label: string;
  slugOverride?: string;
  domain?: string;
  onSave: (slug: string) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(slugOverride ?? slugify(label));
  const save = (): void => onSave(slugify(value));

  return (
    <>
      <html.div style={s.backdrop} onPointerDown={onClose} />
      <html.div
        style={[s.pop, s.at(x, y)]}
        onPointerDown={(e: { stopPropagation: () => void }) =>
          e.stopPropagation()
        }
      >
        <Field.Root>
          <Field.Label>Page URL</Field.Label>
          <InputGroup.Root>
            <InputGroup.Addon position="start">{domain}/</InputGroup.Addon>
            <Input
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  save();
                } else if (e.key === "Escape") {
                  onClose();
                }
              }}
            />
          </InputGroup.Root>
        </Field.Root>
        <html.div style={s.actions}>
          <Button
            variant="link"
            size="small"
            onClick={() => {
              onReset();
              onClose();
            }}
          >
            Reset to name
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={() => {
              save();
              onClose();
            }}
          >
            Save
          </Button>
        </html.div>
      </html.div>
    </>
  );
}
